/**
 * Symmetric cash penny rounding to the nearest nickel (federal guidance).
 */

/** @param {object|null|undefined} businessInfo */
export function isCashPennyRoundingEnabled (businessInfo) {
  return Boolean(businessInfo?.cash_penny_rounding_enabled)
}

/** @param {number|string|null|undefined} amount */
export function roundCashToNickel (amount) {
  const value = Number(amount) || 0
  if (value <= 0) return 0
  if (value < 0.05) return 0.05
  return Math.round(value * 20) / 20
}

/** @param {number|string|null|undefined} exactTotal */
export function cashPennyRoundingAdjustment (exactTotal) {
  const exact = Number(exactTotal) || 0
  const rounded = roundCashToNickel(exact)
  return Math.round((rounded - exact) * 100) / 100
}

/**
 * @param {number|string|null|undefined} exactTotal
 * @param {object|null|undefined} businessInfo
 */
export function cashTotalDue (exactTotal, businessInfo) {
  const exact = Number(exactTotal) || 0
  if (!isCashPennyRoundingEnabled(businessInfo)) {
    return Math.round(exact * 100) / 100
  }
  return roundCashToNickel(exact)
}

/**
 * Amount applied to the tab ledger from cash received (capped at outstanding balance).
 *
 * @param {number|string|null|undefined} receivedAmount
 * @param {number|string|null|undefined} maxTabBalance
 */
export function tabCashApplicationAmount (receivedAmount, maxTabBalance) {
  const received = Math.round((Number(receivedAmount) || 0) * 100) / 100
  const max = Math.round((Number(maxTabBalance) || 0) * 100) / 100
  if (received <= 0) return 0
  if (max > 0) return Math.min(received, max)
  return received
}

/**
 * Minimum cash received for a tab payment application (includes penny rounding when enabled).
 *
 * @param {number|string|null|undefined} appliedAmount
 * @param {object|null|undefined} businessInfo
 */
export function tabCashMinReceived (appliedAmount, businessInfo) {
  return cashTotalDue(appliedAmount, businessInfo)
}

/**
 * Minor units to apply on the tab when linking a register sale payment.
 * Full close-out with cash penny round-up applies the rounded cash collected, not only exact checkout.
 *
 * @param {object} options
 * @param {number} options.intendedMinor Exact checkout / intended tab apply (minor units)
 * @param {number|null} options.paymentMinor Register sale payment (minor units)
 * @param {boolean} options.allowPartial House-tab partial payments
 * @param {boolean} options.closeIfZero Full close-out
 * @param {boolean} options.cashMethod Cash leg
 * @param {object|null|undefined} options.businessInfo
 */
export function tabRegisterLinkAmountMinor ({
  intendedMinor,
  paymentMinor,
  allowPartial,
  closeIfZero,
  cashMethod,
  businessInfo
}) {
  const intended = Math.max(0, Math.round(Number(intendedMinor) || 0))
  if (paymentMinor == null || paymentMinor < 1) {
    return intended
  }
  const payment = Math.round(Number(paymentMinor))
  if (allowPartial) {
    return Math.min(intended, payment)
  }
  if (
    closeIfZero !== false
    && cashMethod
    && isCashPennyRoundingEnabled(businessInfo)
  ) {
    const roundedDue = Math.round(cashTotalDue(intended / 100, businessInfo) * 100)
    if (payment === roundedDue && roundedDue > intended) {
      return payment
    }
  }

  return Math.min(intended, payment)
}

/** @param {number|string|null|undefined} adjustment */
export function formatCashRoundingAdjustment (adjustment) {
  const value = Number(adjustment) || 0
  if (Math.abs(value) < 0.001) return '$0.00'
  const sign = value >= 0 ? '+' : '-'
  return `${sign}$${Math.abs(value).toFixed(2)}`
}
